import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Library, Plus, Trash2, Search, BookmarkPlus, Tag as TagIcon, Eye, Play } from "lucide-react";
import { toast } from "sonner";

/**
 * Task template library — used from the training Sesión tab.
 * Pops a dialog with the user's saved task templates so they can apply
 * one to a given task slot (n=1..3) with a single click.
 */
export default function TaskTemplatePicker({
  onApply,
  trigger,
  saveCurrent,
}) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [activeTags, setActiveTags] = useState([]);
  const [viewTpl, setViewTpl] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = q.trim() ? `?q=${encodeURIComponent(q.trim())}` : "";
      const r = await api.get(`/task-templates${params}`);
      setItems(r.data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, q]);

  // Unique tags across the currently loaded templates, sorted by usage.
  const tagOptions = useMemo(() => {
    const counts = new Map();
    items.forEach((it) => (it.tags || []).forEach((t) => counts.set(t, (counts.get(t) || 0) + 1)));
    return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([t, n]) => ({ tag: t, count: n }));
  }, [items]);

  // Items visible after applying the active tag filter (AND across selected tags).
  const visibleItems = useMemo(() => {
    if (activeTags.length === 0) return items;
    return items.filter((it) => activeTags.every((t) => (it.tags || []).includes(t)));
  }, [items, activeTags]);

  const toggleTag = (t) => {
    setActiveTags((xs) => (xs.includes(t) ? xs.filter((x) => x !== t) : [...xs, t]));
  };

  const apply = async (tpl) => {
    onApply?.(tpl);
    try {
      await api.post(`/task-templates/${tpl.id}/use`);
    } catch (err) {
      // Bumping usage counter is best-effort; log but don't alarm the user
      console.warn("No se pudo registrar uso de la plantilla", err);
    }
    setViewTpl(null);
    setOpen(false);
  };

  const remove = async (tpl) => {
    if (!window.confirm(`¿Eliminar "${tpl.title}" de la biblioteca?`)) return;
    try {
      await api.delete(`/task-templates/${tpl.id}`);
      setItems((xs) => xs.filter((x) => x.id !== tpl.id));
      toast.success("Plantilla eliminada");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <>
      <span onClick={() => setOpen(true)} data-testid="open-task-library">{trigger}</span>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Library className="h-5 w-5 text-purple-600" />
              Biblioteca de tareas
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar por título, descripción o etiqueta..."
                className="pl-9"
                data-testid="task-library-search"
              />
            </div>

            {tagOptions.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5">
                <TagIcon className="h-3.5 w-3.5 text-slate-400" />
                {tagOptions.map(({ tag, count }) => {
                  const active = activeTags.includes(tag);
                  return (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => toggleTag(tag)}
                      className={`text-[11px] px-2 py-0.5 rounded-full border transition font-semibold ${
                        active
                          ? "bg-purple-600 text-white border-purple-600"
                          : "bg-white text-slate-600 border-slate-200 hover:bg-purple-50 hover:border-purple-200"
                      }`}
                      data-testid={`tag-filter-${tag}`}
                    >
                      #{tag}
                      <span className={`ml-1 text-[10px] ${active ? "text-purple-100" : "text-slate-400"}`}>
                        {count}
                      </span>
                    </button>
                  );
                })}
                {activeTags.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setActiveTags([])}
                    className="text-[11px] text-slate-500 hover:text-purple-700 underline ml-1"
                    data-testid="clear-tag-filters"
                  >
                    Limpiar
                  </button>
                )}
              </div>
            )}

            {loading ? (
              <div className="py-6 text-sm text-slate-500">Cargando plantillas...</div>
            ) : visibleItems.length === 0 ? (
              <div className="py-8 text-center text-sm text-slate-400 italic">
                {items.length === 0 ? (
                  <>
                    Aún no tienes tareas guardadas.
                    <br />
                    Guarda una desde la pestaña Sesión pulsando "Guardar en biblioteca".
                  </>
                ) : (
                  <>No hay plantillas con todas las etiquetas seleccionadas.</>
                )}
              </div>
            ) : (
              <ul className="divide-y divide-slate-100 border border-slate-200 rounded-md">
                {visibleItems.map((tpl) => {
                  const imgUrl = tpl.image_id
                    ? `${process.env.REACT_APP_BACKEND_URL}/api/attachments/${tpl.image_id}/file`
                    : null;
                  return (
                  <li
                    key={tpl.id}
                    className="p-3 flex items-start gap-3"
                    data-testid={`task-tpl-${tpl.id}`}
                  >
                    <div className="shrink-0 w-20 h-20 rounded-md border border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center">
                      {imgUrl ? (
                        <img
                          src={imgUrl}
                          alt={tpl.title}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.style.display = "none"; }}
                        />
                      ) : (
                        <Library className="h-6 w-6 text-slate-300" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-slate-900 truncate">{tpl.title}</div>
                      {tpl.objective && (
                        <div className="text-xs text-purple-700 font-semibold mt-0.5">
                          Objetivo: {tpl.objective}
                        </div>
                      )}
                      {tpl.description && (
                        <div className="text-xs text-slate-500 mt-1 line-clamp-2 whitespace-pre-line">
                          {tpl.description}
                        </div>
                      )}
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {(tpl.tags || []).map((t) => (
                          <button
                            type="button"
                            key={t}
                            onClick={(e) => { e.stopPropagation(); toggleTag(t); }}
                            className={`text-[10px] px-1.5 py-0.5 rounded transition ${
                              activeTags.includes(t)
                                ? "bg-purple-600 text-white"
                                : "bg-purple-100 text-purple-700 hover:bg-purple-200"
                            }`}
                            title="Filtrar por esta etiqueta"
                          >
                            #{t}
                          </button>
                        ))}
                        {tpl.team_id ? null : (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
                            compartida
                          </span>
                        )}
                        {tpl.video_url && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 font-bold">
                            🎥 vídeo
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 shrink-0">
                      <Button
                        size="sm"
                        onClick={() => apply(tpl)}
                        className="bg-purple-600 hover:bg-purple-700 text-white font-bold"
                        data-testid={`apply-task-${tpl.id}`}
                      >
                        Usar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setViewTpl(tpl)}
                        className="border-purple-200 text-purple-700 hover:bg-purple-50 font-bold"
                        data-testid={`view-task-${tpl.id}`}
                      >
                        <Eye className="h-3.5 w-3.5 mr-1" /> Ver
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => remove(tpl)}
                        title="Eliminar"
                        data-testid={`delete-task-${tpl.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5 text-red-500" />
                      </Button>
                    </div>
                  </li>
                  );
                })}
              </ul>
            )}

            {saveCurrent && (
              <div className="pt-2 border-t border-slate-100">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full border-purple-300 text-purple-700 hover:bg-purple-50"
                  onClick={async () => {
                    const ok = await saveCurrent();
                    if (ok) {
                      load();
                      toast.success("Guardada en la biblioteca");
                    }
                  }}
                  data-testid="save-current-as-template"
                >
                  <BookmarkPlus className="h-4 w-4 mr-2" /> Guardar la tarea actual en la biblioteca
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Task detail viewer */}
      <Dialog open={!!viewTpl} onOpenChange={(o) => !o && setViewTpl(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="task-detail-dialog">
          {viewTpl && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Library className="h-5 w-5 text-purple-600" />
                  {viewTpl.title}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                {viewTpl.image_id && (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/attachments/${viewTpl.image_id}/file`}
                    alt={viewTpl.title}
                    className="w-full max-h-[45vh] object-contain rounded-md border border-slate-200 bg-slate-50"
                    onError={(e) => { e.target.style.display = "none"; }}
                    data-testid="task-detail-image"
                  />
                )}
                {viewTpl.objective && (
                  <div className="p-3 rounded-md bg-purple-50 border border-purple-100">
                    <div className="text-[10px] uppercase tracking-widest text-purple-700 font-bold mb-0.5">
                      Objetivo
                    </div>
                    <div className="text-sm text-slate-800">{viewTpl.objective}</div>
                  </div>
                )}
                {viewTpl.description && (
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-0.5">
                      Descripción
                    </div>
                    <div className="text-sm text-slate-700 whitespace-pre-line">{viewTpl.description}</div>
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-1.5">
                  {(viewTpl.tags || []).map((t) => (
                    <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">
                      #{t}
                    </span>
                  ))}
                  {!viewTpl.team_id && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
                      compartida
                    </span>
                  )}
                  {viewTpl.times_used > 0 && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                      Usada {viewTpl.times_used} {viewTpl.times_used === 1 ? "vez" : "veces"}
                    </span>
                  )}
                </div>
                {viewTpl.video_url && (
                  <a
                    href={viewTpl.video_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-bold text-rose-600 hover:underline"
                    data-testid="task-detail-video"
                  >
                    <Play className="h-4 w-4" /> Ver vídeo de la tarea
                  </a>
                )}
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setViewTpl(null)} data-testid="task-detail-close">
                  Cerrar
                </Button>
                <Button
                  onClick={() => apply(viewTpl)}
                  className="bg-purple-600 hover:bg-purple-700 text-white font-bold"
                  data-testid="task-detail-use"
                >
                  Usar esta tarea
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * Small inline button: opens the picker for a specific task slot.
 */
export function TaskTemplateButton({ onApply, saveCurrent }) {
  return (
    <TaskTemplatePicker
      onApply={onApply}
      saveCurrent={saveCurrent}
      trigger={
        <Button type="button" size="sm" variant="outline" className="text-xs">
          <Library className="h-3.5 w-3.5 mr-1.5" /> Biblioteca
        </Button>
      }
    />
  );
}
